import { UserItem, BackIcon, CloseIcon, ConfirmDialog } from '@halo/ui-chat-react';
import {
  Box,
  Text,
  Flex,
  Center,
  Button,
  Dialog,
  RadioGroup,
  Stack,
  Field,
  Input,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { Drawer, Switch, DatePicker } from '@halo/ui-react';
import { type GroupMember } from '@halo/chat-sdk';
import { useTranslation } from 'react-i18next';

interface DurationDialogProps {
  trigger?: React.ReactNode;
  onSubmit?: (duration: number) => void;
}

function DurationDialog({ trigger, onSubmit }: DurationDialogProps) {
  const { t } = useTranslation();
  const [loading, _setLoading] = useState(false);
  const [value, setValue] = useState('4');
  const [custom, setCustom] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<{
    duration: string;
  }>();

  const types = [
    {
      label: '1 Hour',
      value: '0',
    },
    {
      label: '4 Hours',
      value: '1',
    },
    {
      label: '1 Day',
      value: '2',
    },
    {
      label: '1 Week',
      value: '3',
    },
    {
      label: 'Forever',
      value: '4',
    },
  ];

  const minDate = useMemo(() => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 150);
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const maxDate = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  }, []);

  return (
    <Dialog.Root placement="center">
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content rounded="xl" w="464px" bg="bg.subtle">
          <Dialog.Header pt="5" px="5" pb="2.5" justifyContent="space-between">
            <Dialog.Title fontWeight="semibold" fontSize="md">
              {t('chatSettings.duration')}
            </Dialog.Title>
          </Dialog.Header>
          <Dialog.Body px="5" pt="0" pb="1">
            <Stack gap="2">
              <Box bg="bg" rounded="xl">
                <RadioGroup.Root
                  value={value}
                  defaultValue="1"
                  onValueChange={(details) => {
                    if (details.value) {
                      setValue?.(details.value);
                      if (details.value !== 'Custom') {
                        setCustom(undefined);
                        reset();
                      }
                    }
                  }}
                >
                  {types.map((item) => (
                    <Box key={item.value} position="relative">
                      <Box
                        position="absolute"
                        bottom="0"
                        left="2"
                        right="2"
                        height="1px"
                        bg="border.subtle"
                      />
                      <RadioGroup.Item value={item.value} px="2" py="3.5" cursor="pointer">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText fontWeight="semibold" fontSize="sm">
                          {item.label}
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                    </Box>
                  ))}
                  <Box w="full">
                    <RadioGroup.Item value="Custom" px="2" py="2.5" cursor="pointer" w="full">
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText
                        w="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        fontWeight="semibold"
                        fontSize="sm"
                      >
                        <Box>Custom</Box>
                        <Box>
                          <form
                            onSubmit={handleSubmit(
                              onSubmit ? (data) => onSubmit(Number(data.duration)) : () => {}
                            )}
                          >
                            <Field.Root
                              invalid={!!errors.duration}
                              flex="1"
                              flexDirection="row"
                              zIndex={1}
                            >
                              <Controller
                                control={control}
                                name="duration"
                                render={({ field }) => (
                                  <DatePicker
                                    name={field.name}
                                    tabIndex={-1}
                                    placeholderText="MM/DD/YYYY"
                                    customInput={<Input size="sm" />}
                                    dateFormat="yyyy/MM/dd"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    onChange={(val: Date | null) => {
                                      const ms = val?.getTime();
                                      setCustom(ms?.toString());
                                      field.onChange(ms || -1);
                                    }}
                                    selected={custom ? new Date(+custom) : undefined}
                                    isClearable
                                    autoComplete="off"
                                  />
                                )}
                              />
                            </Field.Root>
                          </form>
                        </Box>
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  </Box>
                </RadioGroup.Root>
              </Box>
            </Stack>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.ActionTrigger asChild>
              <Button variant="subtle" color="fg" bg="bg.subtle" rounded="xl" minW="96px">
                {t('buttons.cancel')}
              </Button>
            </Dialog.ActionTrigger>
            <Button type="submit" rounded="xl" minW="96px" loading={loading}>
              {t('buttons.save')}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

const NewException = ({
  member,
  open,
  onClose,
  onConfirm,
  onDeleteClick,
}: {
  member?: GroupMember | null;
  open?: boolean;
  onClose?: () => void;
  onConfirm?: (member: GroupMember) => void;
  onDeleteClick?: (member: GroupMember) => void;
}) => {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState(false);

  const handleDeleteClick = useCallback(() => {
    if (!member) return;
    onDeleteClick?.(member);
    setConfirm(false);
  }, [member, onDeleteClick]);
  if (!open || !member) return null;

  return (
    <Drawer.Root open={open} onOpenChange={(e) => !e.open && onClose?.()} lazyMount unmountOnExit>
      <Drawer.Positioner pos="absolute" boxSize="full">
        <Drawer.Content bg="bg.subtle" w="464px" flex="0 0 auto" maxW="none">
          <Drawer.Header justifyContent="space-between" py="2.5" px="4" pt="5" color="fg">
            <Drawer.CloseTrigger asChild pos="relative" left="0" top="0">
              <Flex gap="2" alignItems="center" fontSize="md" cursor="pointer">
                <Center>
                  <BackIcon />
                </Center>
                <Text fontWeight="semibold">{t('chatSettings.newException')}</Text>
              </Flex>
            </Drawer.CloseTrigger>
            <Drawer.CloseTrigger asChild pos="relative" right="0" top="0">
              <Center fontSize="md" cursor="pointer" color="fg.subtle">
                <CloseIcon />
              </Center>
            </Drawer.CloseTrigger>
          </Drawer.Header>
          <Drawer.Body px="4" py="2">
            <Flex gap="3" align="center" my="2" bg="bg" rounded="xl">
              <UserItem
                {...member}
                hoverable={false}
                rippleEffect={false}
                cursor="pointer"
                borderColor="transparent"
              />
            </Flex>

            <Box py="2.5" fontSize="xs" fontWeight="medium" color="fg.muted">
              {t('chatSettings.whatCanThisMemberDo')}
            </Box>

            <Box py="3.5" bg="bg" rounded="xl" px="1" mb="2">
              <Flex rounded="xl" align="center" justify="space-between" cursor="pointer" px="2">
                <Flex align="center" gap="2.5">
                  <Text fontSize="sm" fontWeight="medium">
                    {t('chatSettings.sendMessages')}
                  </Text>
                </Flex>
                <Switch checked={true} disabled />
              </Flex>
            </Box>

            <DurationDialog
              trigger={
                <Box py="3.5" bg="bg" rounded="xl" px="1">
                  <Flex
                    align="center"
                    justify="space-between"
                    cursor="pointer"
                    px="2"
                    fontWeight="medium"
                    onClick={() => {}}
                  >
                    <Flex align="center" gap="2.5">
                      <Text fontSize="sm">{t('chatSettings.duration')}</Text>
                    </Flex>
                    <Box color="brand">{t('chatSettings.forever')}</Box>
                  </Flex>
                </Box>
              }
            />
            <ConfirmDialog
              disabledPortal={false}
              open={confirm}
              onClose={() => setConfirm(false)}
              title={t('chatSettings.deleteException')}
              subTitle={t('chatSettings.deleteExceptionSubTitle')}
              onOk={handleDeleteClick}
              okText={t('buttons.confirm')}
            ></ConfirmDialog>
            <Box py="3.5" bg="bg" rounded="xl" px="1" mt="2" cursor="pointer">
              <Flex
                align="center"
                justify="space-between"
                cursor="pointer"
                px="2"
                fontWeight="medium"
                onClick={() => setConfirm(true)}
              >
                <Flex align="center" gap="2.5">
                  <Text fontSize="sm" color="fg.error">
                    {t('chatSettings.deleteException')}
                  </Text>
                </Flex>
              </Flex>
            </Box>

            <Box mt="8" display="flex" justifyContent="center">
              <Button
                w="116px"
                rounded="xl"
                bg="brand"
                fontWeight="medium"
                onClick={() => onConfirm}
              >
                {t('buttons.apply')}
              </Button>
            </Box>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
};

const MemoizedNewException = memo(NewException);
export { MemoizedNewException as NewException };
